import { ARTIFACT_TYPES } from 'modules/hatch/libs/agent-artifacts';

import type { ArtifactType } from 'modules/hatch/libs/agent-artifacts';

/**
 * One fixed slot in a stock-question list, rendered in a stable order that does
 * not depend on the order the API returns questions in.
 *
 * A question maps into a slot by `template_key` (the backend's stable key for
 * the stock template it was generated from). Rows that predate that field fall
 * back to matching `category`, or — because the backend `category` strings are
 * not a stable contract across environments — to containing one of
 * `textMatchers` in the `question_text`. Fallback matching runs in slot order
 * and the first slot wins, so no matcher may be a substring of another slot's
 * question text. The rail shows the backend `short_label` when present, else the
 * slot's `shortLabel` (kept aligned with the backend catalogue), else the
 * question text; the answer pane shows the full `question_text`.
 */
/**
 * The backend's stock template keys (stak `plugins/dsq/stock/templates.py`).
 * The API field stays an open string so a new backend template renders without
 * a frontend change; this union only types the local catalogue.
 */
export const STOCK_TEMPLATE_KEYS = {
  COMPETITOR_OVERVIEW: 'competitor_overview',
  DIFFERENTIATION: 'differentiation',
  HOW_WE_WIN: 'how_we_win',
  VULNERABILITIES: 'vulnerabilities',
  PRICING_COMPARISON: 'pricing_comparison',
  DISCOVERY_QUESTIONS: 'discovery_questions',
  OBJECTION_HANDLING: 'objection_handling',
  PROOF_POINTS: 'proof_points',
  MAIN_COMPETITORS: 'main_competitors',
  WIN_LOSS_REASONS: 'win_loss_reasons',
  TOP_OBJECTIONS: 'top_objections',
  KEY_DIFFERENTIATORS: 'key_differentiators',
  BUYER_PAIN_POINTS: 'buyer_pain_points',
  COMPETITOR_GAPS: 'competitor_gaps',
} as const;

export type StockTemplateKey =
  (typeof STOCK_TEMPLATE_KEYS)[keyof typeof STOCK_TEMPLATE_KEYS];

export type StockQuestionTemplate = {
  key: StockTemplateKey;
  category: string;
  shortLabel: string;
  textMatchers: readonly string[];
};

/** The 8 competitor/product battlecard slots, in display order. */
export const BATTLECARD_TEMPLATES: readonly StockQuestionTemplate[] = [
  {
    key: STOCK_TEMPLATE_KEYS.COMPETITOR_OVERVIEW,
    category: 'Overview',
    shortLabel: 'Overview',
    textMatchers: ['overview'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.DIFFERENTIATION,
    category: 'Differentiation',
    shortLabel: 'Differentiation',
    textMatchers: ['different', 'why customers choose', 'differentiate'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.HOW_WE_WIN,
    category: 'How We Win',
    shortLabel: 'How we win',
    textMatchers: ['how do we win', 'win against', 'how we win'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.VULNERABILITIES,
    category: 'Vulnerabilities',
    shortLabel: 'Landmines',
    textMatchers: ['vulnerable', 'landmine', 'weakness', 'where are we weak'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.PRICING_COMPARISON,
    category: 'Pricing',
    shortLabel: 'Pricing',
    textMatchers: ['pricing', 'how our price', 'price compare'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.DISCOVERY_QUESTIONS,
    category: 'Discovery',
    shortLabel: 'Discovery questions',
    textMatchers: ['trap-setting', 'trap setting', 'discovery'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.OBJECTION_HANDLING,
    category: 'Objections',
    shortLabel: 'Objections',
    textMatchers: ['objection'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.PROOF_POINTS,
    category: 'Proof Points',
    shortLabel: 'Proof points',
    textMatchers: ['proof point', 'win stories', 'win story'],
  },
];

/** The 6 own-org foundational-info slots, in display order. */
export const FOUNDATIONAL_TEMPLATES: readonly StockQuestionTemplate[] = [
  {
    key: STOCK_TEMPLATE_KEYS.MAIN_COMPETITORS,
    category: 'Competitors',
    shortLabel: 'Competitive landscape',
    textMatchers: ['main competitors', 'top competitors', 'how to position'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.WIN_LOSS_REASONS,
    category: 'Win/Loss',
    shortLabel: 'Win/loss reasons',
    textMatchers: ['win and lose', 'win and why we lose', 'why we win and'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.TOP_OBJECTIONS,
    category: 'Objections',
    shortLabel: 'Top objections',
    textMatchers: ['objection'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.KEY_DIFFERENTIATORS,
    category: 'Differentiators',
    shortLabel: 'Key differentiators',
    textMatchers: ['differentiator', 'unique value'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.BUYER_PAIN_POINTS,
    category: 'Buyer Pain',
    shortLabel: 'Buyer pain points',
    textMatchers: ['pain point', 'use case'],
  },
  {
    key: STOCK_TEMPLATE_KEYS.COMPETITOR_GAPS,
    category: 'Competitor Gaps',
    shortLabel: 'Competitor gaps',
    textMatchers: ['capability gap', 'feature gap'],
  },
];

/**
 * Agent-artifact types considered for "The latest on {entity}". One scoped
 * query fires per type; only types that return at least one item for the
 * entity surface as filter pills. The list is intentionally the discrete
 * (non-rollup) item types so the feed reads as individual recent updates.
 */
export const LATEST_ARTIFACT_TYPES: readonly ArtifactType[] = [
  ARTIFACT_TYPES.NEWS_ITEM,
  ARTIFACT_TYPES.WIN_LOSS_STORY,
  ARTIFACT_TYPES.BLINDSPOTS,
  ARTIFACT_TYPES.OBJECTION_HANDLING,
  ARTIFACT_TYPES.PROSPECT_QUOTE,
];

/** Max recent artifacts shown in "The latest on {entity}". */
export const LATEST_ARTIFACTS_LIMIT = 6;

/** Type pills shown inline before the rest collapse into a "More" menu. */
export const MAX_VISIBLE_TYPE_PILLS = 4;

/** Smart Answers shown per page. */
export const SMART_ANSWERS_PAGE_SIZE = 5;

/** Hover delay (ms) before a truncated-name tooltip opens. */
export const NAME_TOOLTIP_OPEN_DELAY = 500;

/** Character limits for name truncation (full name shown in a tooltip). */
export const COMPETITOR_NAME_MAX = 40;
export const BREADCRUMB_OWNER_MAX = 25;
export const BREADCRUMB_PRODUCT_MAX = 40;

/** Content well width for the detail pages (narrower than the app default). */
export const ENTITY_WELL_MAX_WIDTH = 860;
